import React from "react"
import {FieldErrors, useForm, useWatch} from "react-hook-form"
import {Alert, AlertIcon, Button, IconButton, Image, Tag, TagCloseButton, TagLabel, useToast} from "@chakra-ui/react"

import {IUser} from "@backend/types"
import {FormInput, FSelect} from "@components/form"
import {MenuOption} from "@components/form/FIconPicker"
import {AutoComplete, ResultProps} from "@components/elements"
import {FieldType} from "@components/types"

import globals from "../../../data/globals.json"
import {userApiCalls} from "../service"
import {defaultProfileImg} from "../../../constants"


const fields: FieldType = {
    profilePhoto: {
        field: "publicInfo.profilePhoto",
        label: "Profile Photo",
    },
    email: {
        field: "privateInfo.email",
        label: "Email",
        rules: {
            required: "Email is required"
        }
    },
    displayName: {
        field: "publicInfo.displayName",
        label: "Public Display Name",
        rules: {
            required: "Display Name is required"
        }
    },
    organization: {
        field: "work.organization",
        label: "Organization",
    },
    jobTitle: {
        field: "work.jobTitle",
        label: "Your Job Title ",
    },
    jobTitleOther: {
        field: "work.jobTitleOther",
        label: "Job Title (Other)",
    },
    expertise: {
        field: "work.expertise",
        label: "Your Issue Area Expertise",
    }
}

type FormButtonsProps = {
    errors: FieldErrors,
    isSubmitting: boolean,
    btnTitle?: string
}

function FormButtons({errors, isSubmitting, btnTitle}: FormButtonsProps) {

    return <div className="flex flex-col gap-y-6 px-4 py-6 sm:p-6">
        {Object.keys(errors).length > 0 &&
          <Alert status="error">
            <AlertIcon/>
            <ul>
                {Object.values(errors).map((x, i) => <li key={i}>
                    {x.message as string}
                </li>)}
            </ul>
          </Alert>}
        <div className="flex items-center justify-start">
            <Button type="submit" variant="outline" isLoading={isSubmitting}
                    colorScheme="blue">{btnTitle || "Update"}</Button>
        </div>
    </div>
}

type ProfileFormProps = {
    title: string,
    subTitle?: string | React.ReactNode,
    onSubmit: any,
    errors: FieldErrors,
    isSubmitting: boolean,
    children: React.ReactNode
}

function FormCard({title, subTitle, onSubmit, errors, isSubmitting, children}: ProfileFormProps) {

    return <form
        onSubmit={onSubmit}>
        <div className="px-4 py-6 sm:p-6 lg:pb-8">
            <div>
                {title &&
                  <h2 className="text-lg font-medium leading-6 text-gray-900">{title}</h2>}
                {subTitle &&
                  <p className="mt-1 text-sm text-gray-500">{subTitle}</p>}
            </div>

            <div className="mt-6 flex flex-col lg:flex-row">
                {children}
            </div>
        </div>

        <FormButtons errors={errors} isSubmitting={isSubmitting}/>
    </form>
}

const photoPickerOptions: MenuOption[] = [{
    element: <IconButton
        variant="outline"
        aria-label="image"
        icon={<Image className="relative w-8 h-8" src={defaultProfileImg} alt=""/>}
    />,
    label: "asd",
    value: "123"
}]

type ProfileProps = {
    user: IUser,
    onUpdate?: (val) => void
}

export function ProfileSettings({user, onUpdate}: ProfileProps) {
    const toast = useToast()
    const {
        handleSubmit,
        register,
        setValue,
        getValues,
        formState: {
            errors,
            isSubmitting
        }
    } = useForm({
        defaultValues: {
            publicInfo: user?.publicInfo,
            privateInfo: user?.privateInfo,
        }
    })

    async function onSubmit(values) {
        const {publicInfo} = values
        const result: boolean = await userApiCalls
            .currentUserUpdate({publicInfo})

        toast({
            title: result ? "Account Updated" : "Error",
            status: result ? "success" : "error",
            duration: 3000,
            isClosable: true,
        })

        if (result)
            onUpdate({publicInfo})

    }

    return <FormCard
        title="Profile"
        subTitle="You can update your basic profile information here"
        onSubmit={handleSubmit(onSubmit)}
        errors={errors}
        isSubmitting={isSubmitting}
    >
        <div className="grid grid-cols-2 gap-6">
            <div className="col-span-2 flex-grow lg:mr-6 lg:mt-0 lg:flex-shrink-0 lg:flex-grow-0">
                <p className="text-sm mb-2 font-medium leading-6 text-gray-900" aria-hidden="true">
                    Profile Photo
                </p>
                {/*<FIconPicker*/}
                {/*    title="Select a profile picture"*/}
                {/*    errors={errors}*/}
                {/*    register={register}*/}
                {/*    {...fields.profilePhoto}*/}
                {/*    options={photoPickerOptions}*/}
                {/*    setValue={setValue}*/}
                {/*>*/}
                {/*    <IconButton*/}
                {/*        variant="outline"*/}
                {/*        aria-label="image"*/}
                {/*        size="lg"*/}
                {/*        icon={<Image className="relative w-12 h-12"*/}
                {/*                     src={user.publicInfo.profilePhoto || defaultProfileImg}*/}
                {/*                     alt=""/>}*/}
                {/*    />*/}
                {/*</FIconPicker>*/}
                <Image className="relative w-12 h-12"
                       src={user.publicInfo.profilePhoto || defaultProfileImg}
                       alt=""/>

            </div>

            <FormInput className="col-span-2" register={register}
                       errors={errors} inputOptions={{
                disabled: true
            }} {...fields.email} />

            <FormInput className="col-span-2" register={register}
                       errors={errors}
                       {...fields.displayName} />
        </div>

    </FormCard>
}

export function PasswordUpdate({user}: ProfileProps) {
    const toast = useToast()
    const {
        handleSubmit,
        register,
        getValues,
        reset,
        formState: {
            errors,
            isSubmitting
        }
    } = useForm({})

    async function onSubmit(values) {

        const {currentPassword, password} = values
        let saveResult: {
            status: "success" | "error",
            title: string,
        } | null
        try {
            const result = await userApiCalls.currentUserPasswordUpdate({currentPassword, password})
            saveResult = {
                status: result ? "success" : "error",
                title: result ? `Password updated successfully.` : "Error",
            }
        } catch (err) {
            saveResult = {
                status: "error",
                title: err.message
            }
        }

        toast({
            ...saveResult,
            duration: 3000,
            isClosable: true,
        })

        if (saveResult.status === "success") {
            reset()
        }

    }

    async function onResetPasswordLinkClick() {
        try {
            const result = await userApiCalls.sendPasswordResetLink()
            toast({
                status: "success",
                title: result,
                duration: 3000,
                isClosable: true,
            })
        } catch (err) {
            toast({
                status: "error",
                title: err.message,
                duration: 3000,
                isClosable: true,
            })
        }


    }

    return <FormCard
        title="Password Update"
        subTitle={<span>You can update your password or <Button size="sm" colorScheme="blue"
                                                                onClick={onResetPasswordLinkClick}
                                                                variant="link">send a reset password link</Button> </span>}
        onSubmit={handleSubmit(onSubmit)}
        errors={errors}
        isSubmitting={isSubmitting}
    >
        <div className="grid grid-cols-2 gap-6">

            {user.security?.password === "hasPass" ?
                <FormInput inputOptions={{type: "password"}} className="col-span-2"
                           register={register}
                           errors={errors} field="currentPassword"
                           rules={{
                               required: true
                           }}
                           label="Current Password"/> :
                <Alert status="info" className="col-span-2">
                    <AlertIcon/>
                    You currently don't have a local password.
                    For added security, consider setting one up now.
                </Alert>
            }
            <FormInput inputOptions={{type: "password"}} className="col-span-2" register={register}
                       rules={{
                           required: true,
                           minLength: {
                               value: 6,
                               message: "Password must me at least 6 characters"
                           },
                       }}
                       errors={errors} field="password" label="New Password"/>
            <FormInput inputOptions={{type: "password"}} className="col-span-2" register={register}
                       rules={{
                           required: true,
                           minLength: {
                               value: 6,
                               message: "Password confirm must me at least 6 characters"
                           },
                           validate: val => val !== getValues("password") ? "Passwords do not match" : true
                       }}
                       errors={errors} field="confirmPassword" label="Confirm Password"/>
            {Object.keys(user.socials || {}).length > 0 &&
              <div className="mt-6 flex-grow lg:mr-6 lg:mt-0 lg:flex-shrink-0 lg:flex-grow-0">
                <p className="text-sm mb-2 font-medium leading-6 text-gray-900" aria-hidden="true">
                  Connected Social Logins
                </p>
                <ul role="list" className="divide-y divide-gray-200">
                    {Object.keys(user.socials).map((key) =>
                        <li key={key} className="flex py-4">
                            <img className="h-10 w-10 rounded-full"
                                 src={user.socials[key].profilePhoto || defaultProfileImg}
                                 alt=""/>
                            <div className="ml-3 flex items-center">
                                <span className="text-sm font-medium text-gray-900 capitalize">{key}</span>
                            </div>
                        </li>)}


                </ul>
              </div>}
        </div>
    </FormCard>
}

const expertiseList = globals.userProfile.workOptions.expertise.items.map(x => ({
    label: x,
    value: x,
    element: <span>{x}</span>,
    data: x
}))

export function OrganizationSettings({user, onUpdate}: ProfileProps) {
    const toast = useToast()
    const {
        handleSubmit,
        register,
        control,
        getValues,
        setValue,
        formState: {
            errors,
            isSubmitting
        }
    } = useForm({
        defaultValues: {
            work: user?.work
        }
    })


    async function onSubmit(values) {
        const {work} = values

        if (work.jobTitle !== "Other") {
            work.jobTitleOther = null
        }

        const saveData = Object.keys(work).reduce((acc, key) => {

            if (!work[key] || (Array.isArray(work[key]) && !work[key].length))
                return acc

            acc[key] = work[key]

            return acc
        }, {})


        const result: boolean = await userApiCalls
            .currentUserUpdate({work: saveData})

        toast({
            title: result ? "Account Updated" : "Error",
            status: result ? "success" : "error",
            duration: 3000,
            isClosable: true,
        })
        if (result)
            onUpdate({work})

    }


    return <FormCard
        title="Organization"
        subTitle="You can update your work info here"
        onSubmit={handleSubmit(onSubmit)}
        errors={errors}
        isSubmitting={isSubmitting}
    >

        <OrganizationForm
            register={register}
            errors={errors}
            control={control}
            setValue={setValue}
            work={user?.work}
        />

    </FormCard>
}

type OrganizationFormProps = {
    register: any,
    errors: any,
    control: any,
    setValue: any,
    work?: any
}

export function OrganizationForm({register, errors, control, setValue, work}: OrganizationFormProps) {

    async function OnExpSearch(searchText): Promise<ResultProps[]> {

        if (!searchText)
            return expertiseList

        const pattern = new RegExp(`.*${searchText}.*`, "i")

        return expertiseList.filter(x => pattern.test(x.value))
    }

    const jobTitle = useWatch({
        name: fields.jobTitle.field,
        control,
        defaultValue: work?.jobTitle
    })

    const expertise = useWatch({
        name: fields.expertise.field,
        control,
        defaultValue: work?.expertise || []
    })


    register(fields.expertise.field)


    return <div className="grid grid-cols-2 gap-6">
        <FormInput className="col-span-2" register={register}
                   errors={errors} {...fields.organization} />

        <div className="col-span-2 grid grid-cols-2 gap-6">
            <FSelect
                {...fields.jobTitle}
                errors={errors}
                className={"col-span-1"}
                register={register}
                options={[
                    ...globals.userProfile.workOptions.jobTitle.items.map(x => ({label: x, value: x})),
                    {label: "Other", value: "Other"}
                ]}

            />

            {jobTitle === "Other" &&
              <FormInput className="col-span-1" register={register}
                         errors={errors} {...fields.jobTitleOther} />
            }
        </div>

        <AutoComplete
            className="col-span-2"
            label={fields.expertise.label}
            defaultValues={expertiseList}
            onSearch={OnExpSearch}
            onSelect={(val) => {
                if (!expertise.includes(val.value))
                    setValue(fields.expertise.field, [...expertise, val.value])
            }}
        />
        {expertise.length > 0 &&
          <div className="flex flex-col justify-start items-start space-y-2">
              {expertise.map(x =>
                  <Tag
                      borderRadius="full"
                      colorScheme="green"
                  >
                      <TagLabel>{x}</TagLabel>
                      <TagCloseButton
                          onClick={() => {
                              const newExp = expertise.filter(exp => exp !== x)
                              setValue(fields.expertise.field, newExp)
                          }}/>
                  </Tag>)}
          </div>}
    </div>
}